import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { AgeGroupPublic } from "@dugads/shared";

import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMediaQuery } from "@/lib/use-media-query";

const EVENT_TYPE_OPTIONS: Array<[string, string]> = [
  ["match_day", "Match day"],
  ["practice", "Practice"],
  ["tryout", "Tryout"],
];

export default function EventCreatePage() {
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [eventType, setEventType] = useState("match_day");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [ageGroupId, setAgeGroupId] = useState(""); // 150 -- trials target an age group
  const [fromTemplate, setFromTemplate] = useState(false); // 192 -- open the template picker on the new draft

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [schoolName, setSchoolName] = useState("");
  const [ageGroups, setAgeGroups] = useState<AgeGroupPublic[]>([]);

  useEffect(() => {
    if (!schoolId) return;
    api
      .getSchool(schoolId)
      .then((res) => setSchoolName(res.school.name))
      .catch(() => {});
    api
      .getAllAgeGroups()
      .then((res) => setAgeGroups(res.age_groups))
      .catch(() => {});
  }, [schoolId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!schoolId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await api.createEvent(schoolId, {
        name,
        event_type: eventType,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        age_group_id: eventType === "tryout" && ageGroupId ? ageGroupId : null,
      });
      navigate(`/events/${res.event.id}${fromTemplate ? "?applyTemplate=1" : ""}`);
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to create event",
      );
      setLoading(false);
    }
  }

  // Mobile: an iOS-style grouped form (segmented type, grouped when rows,
  // full-width actions). Same state + submit as desktop.
  if (!isMdUp) {
    return (
      <div className="flex flex-col gap-5" data-page="event-create" data-state={loading ? "submitting" : error ? "error" : "ready"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5" data-region="form" data-form="event-create">
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}

          <div className="flex flex-col gap-1.5">
            <span className="px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Event name</span>
            <input
              data-field="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. U16A vs Rival High"
              required
              className="h-11 rounded-2xl border border-border bg-card px-4 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Type</span>
            <div className="flex rounded-xl bg-muted p-0.5">
              {EVENT_TYPE_OPTIONS.map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  data-field="event-type"
                  data-value={v}
                  onClick={() => setEventType(v)}
                  className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
                    eventType === v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {eventType === "tryout" && (
            <div className="flex flex-col gap-1.5">
              <span className="px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Age group (optional)</span>
              <select
                title="Age Group"
                data-field="age-group"
                value={ageGroupId}
                onChange={(e) => setAgeGroupId(e.target.value)}
                className="h-11 rounded-2xl border border-border bg-card px-4 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">No specific age group</option>
                {ageGroups.map((ag) => (
                  <option key={ag.id} value={ag.id}>{ag.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <span className="px-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">When</span>
            <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <label className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm font-medium">Starts</span>
                <input
                  type="datetime-local"
                  data-field="starts-at"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  required
                  className="min-w-0 flex-1 bg-transparent text-right text-sm focus-visible:outline-none"
                />
              </label>
              <label className="flex items-center justify-between gap-3 px-4 py-3">
                <span className="text-sm font-medium">Ends</span>
                <input
                  type="datetime-local"
                  data-field="ends-at"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  required
                  className="min-w-0 flex-1 bg-transparent text-right text-sm focus-visible:outline-none"
                />
              </label>
            </div>
          </div>

          <label className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3 shadow-sm">
            <span className="text-sm font-medium">Start from a saved template</span>
            <input
              type="checkbox"
              data-field="from-template"
              checked={fromTemplate}
              onChange={(e) => setFromTemplate(e.target.checked)}
              className="h-5 w-5 accent-primary"
            />
          </label>

          <div className="mt-2 flex flex-col gap-2">
            <button
              type="submit"
              data-action="submit"
              disabled={loading}
              className="h-12 rounded-2xl bg-primary text-sm font-semibold text-primary-foreground shadow-sm transition-transform active:scale-[.99] disabled:opacity-60"
            >
              {loading ? "Creating..." : "Create event"}
            </button>
            <button
              type="button"
              data-action="cancel"
              onClick={() => navigate(`/schools/${schoolId}/events`)}
              className="h-12 rounded-2xl border border-border bg-card text-sm font-semibold text-muted-foreground shadow-sm transition-transform active:scale-[.99]"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-page="event-create" data-state={loading ? "submitting" : error ? "error" : "ready"}>
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to={`/schools/${schoolId}`} className="hover:underline">
            {schoolName || "School"}
          </Link>
          <span>/</span>
          <Link
            to={`/schools/${schoolId}/events`}
            className="hover:underline"
          >
            Events
          </Link>
          <span>/</span>
          <span>New</span>
        </div>
        <h1 className="text-2xl font-semibold">New Event</h1>
        <p className="text-muted-foreground">
          Schedule a match day, practice, or tryout
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex max-w-md flex-col gap-4" data-region="form" data-form="event-create">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <div className="flex flex-col gap-2">
          <Label htmlFor="name">Event Name</Label>
          <Input
            id="name"
            data-field="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. U16A vs Rival High"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="event_type">Event Type</Label>
          <select
            id="event_type"
            data-field="event-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            required
          >
            <option value="match_day">Match Day</option>
            <option value="practice">Practice</option>
            <option value="tryout">Tryout</option>
          </select>
        </div>

        {/* 150 -- trials target an age group with no teams ("no teams, just age group") */}
        {eventType === "tryout" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="age_group">Age Group (optional)</Label>
            <select
              id="age_group"
              title="Age Group"
              data-field="age-group"
              value={ageGroupId}
              onChange={(e) => setAgeGroupId(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">No specific age group</option>
              {ageGroups.map((ag) => (
                <option key={ag.id} value={ag.id}>
                  {ag.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Trials can target an age group without attaching teams.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <Label htmlFor="starts_at">Start Time</Label>
          <Input
            id="starts_at"
            data-field="starts-at"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="ends_at">End Time</Label>
          <Input
            id="ends_at"
            data-field="ends-at"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            required
          />
        </div>

        {/* 192 -- hand off to the start-from-template picker on the new draft */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-field="from-template"
            checked={fromTemplate}
            onChange={(e) => setFromTemplate(e.target.checked)}
            className="h-4 w-4 accent-primary"
          />
          Start from a saved template
        </label>

        <div className="flex gap-3">
          <Button type="submit" data-action="submit" disabled={loading}>
            {loading ? "Creating..." : "Create Event"}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-action="cancel"
            onClick={() => navigate(`/schools/${schoolId}/events`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
